import React, { useEffect, useState, useMemo } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
  Save,
  Send,
} from 'lucide-react'
import RecordAustracSubmissionModal from './RecordAustracSubmissionModal'
import SmrPreviewModal from '../../../components/SmrPreviewModal'
import { useSmrFormPanel, getSubmitBlockers, smrReportDeadlineOverdue, SMR_P } from './smrFormPanelCore'
import { formatSydneyTime } from '../../../utils/sydneyDateTime'
import {
  SmrV2PartMatter,
  SmrV2PartEntity,
  SmrV2PartSubject,
  SmrV2PartOtherParties,
  SmrV2PartUnidentified,
  SmrV2PartTransactions,
  SmrV2PartAdditional,
} from './SmrFormPanelV2Parts'

const PARTS = [
  { id: 'A', label: 'Details of the Matter', short: 'Matter' },
  { id: 'B', label: 'Reporting Entity', short: 'Entity' },
  { id: 'C', label: 'Suspicious Person', short: 'Subject' },
  { id: 'D', label: 'Other Parties', short: 'Others' },
  { id: 'E', label: 'Unidentified', short: 'Unid.' },
  { id: 'F', label: 'Transactions', short: 'Txns' },
  { id: 'G', label: 'Additional Details', short: 'Narrative' },
]

/**
 * AUSTRAC-style Part A–G layout with the same draft API as SmrFormPanel.
 * Familiar tab flow for users who file via AUSTRAC Online; investigation data is surfaced in Part G.
 */
export default function SmrFormPanelV2({ caseId, caseNumber, patronName, memberId, onFiled, archivalMode = false, apiMode = 'case', formApiRef, onDraftChange }) {
  const [activePart, setActivePart] = useState('A')
  const [narrativeGuide, setNarrativeGuide] = useState(false)
  const [findingsDetail, setFindingsDetail] = useState(false)

  const {
    draft,
    source,
    loading,
    saving,
    autoSaving,
    lastAutoSaved,
    previousSmrCount,
    submitting,
    submittedForReview,
    dirty,
    showPreview,
    setShowPreview,
    downloadingPdf,
    submittingToAustrac,
    austracRef,
    setAustracRef,
    showAustracModal,
    openAustracModal,
    closeAustracModal,
    filingMethod,
    setFilingMethod,
    filedAtLocal,
    setFiledAtLocal,
    filingNotes,
    setFilingNotes,
    addToWatchlist,
    setAddToWatchlist,
    onWatchlist,
    watchlistLoading,
    confirmationImage,
    setConfirmationImage,
    load,
    updatePath,
    toggleB2,
    toggleB4,
    setFormat,
    updateTransaction,
    addTransactionRow,
    removeTransactionRow,
    addOnlineRow,
    updateOnlineRow,
    addAgencyRow,
    updateAgencyRow,
    downloadEvidenceFile,
    persistDraft,
    checklist,
    saveDraft,
    submitForReview,
    handleDownloadPdf,
    handleSubmitToAustrac,
  } = useSmrFormPanel({ caseId, memberId, onFiled, apiMode })

  useEffect(() => {
    onDraftChange?.(draft)
  }, [draft, onDraftChange])

  useEffect(() => {
    if (!formApiRef) return undefined
    formApiRef.current = { persistDraft, draft, dirty }
    return () => {
      formApiRef.current = null
    }
  }, [formApiRef, persistDraft, draft, dirty])

  const formFormat = draft?.form_format === 'rules_2025' ? 'rules_2025' : 'legacy'
  const completionPct = useMemo(
    () => (checklist.total ? Math.round((checklist.passed / checklist.total) * 100) : 0),
    [checklist.passed, checklist.total]
  )

  const partIndex = PARTS.findIndex((p) => p.id === activePart)
  const goPrev = () => {
    if (partIndex > 0) setActivePart(PARTS[partIndex - 1].id)
  }
  const goNext = () => {
    if (partIndex < PARTS.length - 1) setActivePart(PARTS[partIndex + 1].id)
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500 gap-2 rounded-xl border border-slate-200 bg-slate-50/50">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: SMR_P.pri }} />
        <span className="text-sm">Loading SMR draft…</span>
      </div>
    )
  }

  if (!draft) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-600 text-sm bg-white">
        No SMR draft available for this case.
        <button
          type="button"
          onClick={() => load(true)}
          className="block mx-auto mt-3 text-sm font-semibold underline"
          style={{ color: SMR_P.pri }}
        >
          Regenerate from investigation
        </button>
      </div>
    )
  }

  const ri = draft.reportInfo || {}
  const co = draft.completingOfficer || {}
  const sc = draft.suspicionContact || {}
  const meta = draft.meta || {}
  const dv = meta.dateRangeValidation
  const deadlineOverdue = smrReportDeadlineOverdue(meta)
  const submitBlockers = getSubmitBlockers(checklist, dv)
  const submitBlocked = submitBlockers.length > 0

  const activeMeta = PARTS.find((p) => p.id === activePart)

  const partBody = (() => {
    switch (activePart) {
      case 'A':
        return (
          <SmrV2PartMatter
            draft={draft}
            formFormat={formFormat}
            updatePath={updatePath}
            toggleB4={toggleB4}
            ri={ri}
          />
        )
      case 'B':
        return (
          <SmrV2PartEntity
            draft={draft}
            formFormat={formFormat}
            updatePath={updatePath}
            ri={ri}
            co={co}
            sc={sc}
            previousSmrCount={previousSmrCount}
          />
        )
      case 'C':
        return <SmrV2PartSubject draft={draft} formFormat={formFormat} updatePath={updatePath} />
      case 'D':
        return <SmrV2PartOtherParties draft={draft} />
      case 'E':
        return <SmrV2PartUnidentified draft={draft} memberId={memberId} updatePath={updatePath} />
      case 'F':
        return (
          <SmrV2PartTransactions
            draft={draft}
            formFormat={formFormat}
            updateTransaction={updateTransaction}
            removeTransactionRow={removeTransactionRow}
            addTransactionRow={addTransactionRow}
          />
        )
      case 'G':
        return (
          <SmrV2PartAdditional
            draft={draft}
            formFormat={formFormat}
            updatePath={updatePath}
            toggleB2={toggleB2}
            narrativeGuide={narrativeGuide}
            setNarrativeGuide={setNarrativeGuide}
            findingsDetail={findingsDetail}
            setFindingsDetail={setFindingsDetail}
            addAgencyRow={addAgencyRow}
            updateAgencyRow={updateAgencyRow}
            addOnlineRow={addOnlineRow}
            updateOnlineRow={updateOnlineRow}
            downloadEvidenceFile={downloadEvidenceFile}
            checklist={checklist}
          />
        )
      default:
        return null
    }
  })()

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-[#f8fafb] shadow-sm">
      {/* AUSTRAC-style header */}
      <div
        className="px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        style={{
          background: `linear-gradient(135deg, ${SMR_P.pri} 0%, ${SMR_P.priD} 100%)`,
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-extrabold shrink-0"
            style={{ background: SMR_P.acc, color: SMR_P.priD }}
          >
            SMR
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-white truncate">Suspicious Matter Report</div>
            <div className="text-xs text-white/60">AUSTRAC-style layout — Section 41 AML/CTF Act 2006</div>
            <div className="text-[11px] text-white/50 mt-1 truncate">
              {caseNumber && <span className="font-mono mr-2">{caseNumber}</span>}
              {patronName}
              {memberId ? <span className="font-mono ml-1">· {memberId}</span> : null}
              {source && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-white/15 capitalize">{source}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-white/50">Checklist</div>
            <div className="text-lg font-bold tabular-nums" style={{ color: SMR_P.acc }}>
              {completionPct}%
            </div>
          </div>
          <div className="w-20 h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${completionPct}%`, background: SMR_P.acc }} />
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/30 text-xs font-semibold text-white hover:bg-white/10"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Regenerate
          </button>
        </div>
      </div>

      {/* Format + deadline toolbar */}
      <div className="bg-white border-b border-slate-100 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-xs font-semibold text-slate-600">Format</span>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          <button
            type="button"
            onClick={() => setFormat('legacy')}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
              formFormat === 'legacy' ? 'text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            style={formFormat === 'legacy' ? { background: SMR_P.pri } : {}}
          >
            Legacy
          </button>
          <button
            type="button"
            onClick={() => setFormat('rules_2025')}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
              formFormat === 'rules_2025' ? 'text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            style={formFormat === 'rules_2025' ? { background: SMR_P.pri } : {}}
          >
            Rules 2025
          </button>
        </div>
        <div className={`text-xs sm:ml-auto ${deadlineOverdue ? 'text-red-700' : 'text-slate-500'}`}>
          <span className="font-semibold">Filing deadline: </span>
          <span className={deadlineOverdue ? 'font-bold' : 'font-semibold text-slate-700'}>{meta.reportDeadline || '—'}</span>
          {deadlineOverdue && (
            <span className="ml-2 text-[10px] font-bold uppercase text-red-800 bg-red-100 px-1.5 py-0.5 rounded border border-red-200">
              Overdue
            </span>
          )}
        </div>
      </div>

      {deadlineOverdue && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          <div className="font-bold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Filing deadline has passed — complete A10 and confirm A7–A9.
          </div>
        </div>
      )}

      {dv && (
        <div
          className={`mx-4 mt-3 rounded-lg border px-3 py-2 text-sm ${
            dv.valid ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'
          }`}
        >
          <div className="font-bold flex items-center gap-2">
            {dv.valid ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            Transaction date range (3-day rule)
          </div>
          <p className="mt-1 text-xs opacity-90">{dv.message}</p>
        </div>
      )}

      {/* Part tabs */}
      <div className="bg-white border-b border-slate-100 px-2 sm:px-3 flex gap-0 overflow-x-auto scrollbar-thin">
        {PARTS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActivePart(p.id)}
            className={`shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-[13px] font-semibold border-b-2 transition-colors ${
              activePart === p.id ? 'text-[#0a5169] border-[#0a5169]' : 'text-slate-400 border-transparent hover:text-slate-600'
            }`}
          >
            <span className="hidden sm:inline">Part {p.id} · </span>
            <span className="sm:hidden"> {p.id} </span>
            <span className="hidden md:inline">{p.label}</span>
            <span className="md:hidden">{p.short}</span>
          </button>
        ))}
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 pb-28">
        <div className="bg-white rounded-lg border border-slate-100 px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Part {activePart}</div>
            <div className="text-base font-bold text-slate-800">{activeMeta?.label}</div>
          </div>
          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-sky-50 text-sky-800 border border-sky-100">
            SVCP draft
          </span>
        </div>

        <div className="bg-white rounded-lg border border-slate-100 p-4 sm:p-5">{partBody}</div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
          <button
            type="button"
            onClick={goPrev}
            disabled={partIndex <= 0}
            className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 disabled:opacity-40 disabled:cursor-default"
          >
            ← Previous part
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={partIndex >= PARTS.length - 1}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 sm:order-last"
            style={{ background: SMR_P.pri }}
          >
            Next part →
          </button>
        </div>
      </div>

      {submitBlocked && (
        <div id="smr-submit-blockers" className="sticky bottom-[4.5rem] left-0 right-0 z-10 mx-0 border-t border-amber-200 bg-amber-50 px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
          <div className="max-w-3xl mx-auto">
            <div className="font-semibold text-amber-950 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Complete these items before submit for review
            </div>
            <ul className="mt-2 space-y-1.5 text-sm text-amber-900">
              {submitBlockers.map((b) => (
                <li key={`${b.kind}-${b.label}`} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-amber-700">○</span>
                  <span>{b.label}</span>
                  {b.part && (
                    <button
                      type="button"
                      onClick={() => setActivePart(b.part)}
                      className="text-xs font-semibold underline decoration-amber-600/60 hover:text-amber-950"
                    >
                      Go to Part {b.part}
                    </button>
                  )}
                  {b.detail && <span className="w-full text-xs text-amber-800/90 pl-5">{b.detail}</span>}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-amber-800/80">
              Full checklist is at the bottom of Part G. Required officer and contact fields are in Part B; identity in Part C; transactions in Part F.
            </p>
          </div>
        </div>
      )}

      {/* Sticky action bar */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        <div className="flex flex-col gap-0.5 text-xs text-slate-600">
          {dirty && <span className="font-medium text-amber-700">Unsaved changes</span>}
          {autoSaving && (
            <span className="flex items-center gap-1 text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              Auto-saving…
            </span>
          )}
          {!dirty && lastAutoSaved && !autoSaving && (
            <span className="text-slate-400">Draft synced {formatSydneyTime(new Date(lastAutoSaved))}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            disabled={saving || !dirty}
            onClick={saveDraft}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save draft
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            type="button"
            disabled={downloadingPdf}
            onClick={() => handleDownloadPdf(caseNumber)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
          {!archivalMode ? (
            <button
              type="button"
              disabled={submitting || submitBlocked || submittedForReview}
              onClick={submitForReview}
              aria-describedby={submitBlocked ? 'smr-submit-blockers' : undefined}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: submittedForReview ? '#6b7280' : SMR_P.pri }}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : submittedForReview ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {submitting ? 'Submitting…' : submittedForReview ? 'Submitted for review' : 'Submit for review'}
            </button>
          ) : null}
        </div>
      </div>

      {!archivalMode ? (
      <>
      <div className="border-t border-slate-200 bg-slate-50 px-4 py-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-slate-800">Record AUSTRAC filing</div>
            <div className="text-xs text-slate-500 mt-0.5">Lodge the report in AUSTRAC Online first, then record the reference number here.</div>
          </div>
          <button
            type="button"
            onClick={openAustracModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shrink-0"
            style={{ background: '#dc2626' }}
          >
            <FileText className="w-4 h-4" />
            AUSTRAC submission
          </button>
        </div>
      </div>

      <RecordAustracSubmissionModal
        open={showAustracModal}
        onClose={closeAustracModal}
        onSubmit={handleSubmitToAustrac}
        submitting={submittingToAustrac}
        austracRef={austracRef}
        setAustracRef={setAustracRef}
        filingMethod={filingMethod}
        setFilingMethod={setFilingMethod}
        filedAtLocal={filedAtLocal}
        setFiledAtLocal={setFiledAtLocal}
        filingNotes={filingNotes}
        setFilingNotes={setFilingNotes}
        addToWatchlist={addToWatchlist}
        setAddToWatchlist={setAddToWatchlist}
        onWatchlist={onWatchlist}
        watchlistLoading={watchlistLoading}
        memberId={memberId}
        confirmationImage={confirmationImage}
        setConfirmationImage={setConfirmationImage}
      />
      </>
      ) : null}

      <SmrPreviewModal caseId={caseId} isOpen={showPreview} onClose={() => setShowPreview(false)} />
    </div>
  )
}
